/**
 * Four destinations, and that is the ceiling. A till that offers more than four is a till that
 * has stopped being a till.
 *
 * Nav items are permission-driven from the same constants the routes declare, so a nav item and
 * its route can never disagree about who may use it.
 */

import { NavLink } from 'react-router';

import { Permission, can, selectUser } from '@pos/shared';

import { useAppSelector } from '@/app/hooks';
import { warmOn } from '@/shared/lib/prefetch';
import { cn } from '@/shared/ui';

type Item = {
  to: string;
  label: string;
  permission: string | null;
  warm?: () => void;
  icon: (active: boolean) => React.ReactNode;
};

const ITEMS: Item[] = [
  { to: '/sell', label: 'Sell', permission: Permission.SaleCreate, icon: CartIcon },
  { to: '/sales', label: 'My sales', permission: Permission.SaleViewOwn, warm: warmOn('sales'), icon: ListIcon },
  { to: '/shift', label: 'My cash', permission: Permission.ShiftViewOwn, warm: warmOn('shift'), icon: CashIcon },
  { to: '/profile', label: 'Profile', permission: null, icon: PersonIcon },
];

export function ShellNav() {
  const user = useAppSelector(selectUser);

  const visible = ITEMS.filter((item) => item.permission === null || can(user, item.permission));

  // One destination is not a choice, and a bar showing it is chrome with no purpose.
  if (visible.length < 2) return null;

  return (
    <nav
      className="flex border-t border-line-subtle pb-[env(safe-area-inset-bottom)]"
      aria-label="Main"
      data-print="hide"
    >
      {visible.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          data-tour={`tab${item.to.replace('/', '-')}`}
          // Warmed on hover and on touch-down, so the chunk is usually already there by the time
          // the tap completes.
          onPointerEnter={item.warm}
          onTouchStart={item.warm}
          className={({ isActive }) =>
            cn(
              'flex min-h-touch flex-1 flex-col items-center justify-center gap-1 py-2 text-xs font-semibold',
              'transition-colors duration-fast ease-standard',
              isActive ? 'text-brand' : 'text-content-muted',
            )
          }
        >
          {({ isActive }) => (
            <>
              {item.icon(isActive)}
              <span>{item.label}</span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

const stroke = (active: boolean) => ({
  stroke: 'currentColor',
  strokeWidth: active ? 2.1 : 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  fill: 'none',
});

function CartIcon(active: boolean) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 4h2l2.2 10.4a2 2 0 0 0 2 1.6h7.3a2 2 0 0 0 2-1.5L20 7H6" {...stroke(active)} />
      <circle cx="10" cy="20" r="1.3" {...stroke(active)} />
      <circle cx="17" cy="20" r="1.3" {...stroke(active)} />
    </svg>
  );
}

function ListIcon(active: boolean) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 7h12M8 12h12M8 17h12M4 7h.01M4 12h.01M4 17h.01" {...stroke(active)} />
    </svg>
  );
}

function PersonIcon(active: boolean) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="8" r="3.5" {...stroke(active)} />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" {...stroke(active)} />
    </svg>
  );
}

/** A note, folded. Reads as money rather than as a coin, which is what this shop handles. */
function CashIcon(active: boolean) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-6 w-6">
      <rect
        x="2.5"
        y="6"
        width="19"
        height="12"
        rx="2"
        stroke="currentColor"
        strokeWidth={active ? 2 : 1.6}
      />
      <circle cx="12" cy="12" r="2.75" stroke="currentColor" strokeWidth={active ? 2 : 1.6} />
      <path d="M6 9v6M18 9v6" stroke="currentColor" strokeWidth={active ? 2 : 1.6} strokeLinecap="round" />
    </svg>
  );
}
